/**
 * Feature orchestration: read from the email, build the prompt, call the LLM,
 * and optionally write back. Each function returns the LLM's text so the UI
 * can preview it before applying.
 */
import { LlmClient } from "@/llm/client";
import {
  buildDraftPrompt,
  buildProofreadPrompt,
  buildSummarizePrompt,
  buildTonePrompt,
  buildTranslatePrompt,
} from "@/llm/prompts";
import { insertText, readBody, readHeaders, setBody } from "@/office/integration";
import type { FeatureId, LlmConfig, ToneStyle } from "@/types";

export class FeatureService {
  private client: LlmClient;
  constructor(cfg: LlmConfig) {
    this.client = new LlmClient(cfg);
  }

  /** Draft a reply or new message. Writes the result into the compose body if requested. */
  async draft(instruction: string, apply: boolean): Promise<string> {
    const [body, headers] = await Promise.all([readBody(), readHeaders()]);
    const text = await this.client.complete(
      buildDraftPrompt({ incoming: body, instruction, headers }),
    );
    if (apply) await setBody(text);
    return text;
  }

  /** Translate the given text (or current body) to a language. */
  async translate(text: string, targetLang: string): Promise<string> {
    const src = text || (await readBody());
    return this.client.complete(buildTranslatePrompt(src, targetLang));
  }

  /** Summarize the current email/thread. */
  async summarize(): Promise<string> {
    const body = await readBody();
    return this.client.complete(buildSummarizePrompt(body));
  }

  /** Rewrite the given text's tone; optionally insert back at cursor. */
  async tone(text: string, tone: ToneStyle, apply: boolean): Promise<string> {
    const src = text || (await readBody());
    const out = await this.client.complete(buildTonePrompt(src, tone));
    if (apply) await insertText(out);
    return out;
  }

  /** Proofread the given text; optionally replace at cursor. */
  async proofread(text: string, apply: boolean): Promise<string> {
    const src = text || (await readBody());
    const out = await this.client.complete(buildProofreadPrompt(src));
    if (apply) await insertText(out);
    return out;
  }
}

/** Dispatch helper used by the UI. */
export async function runFeature(
  feature: FeatureId,
  cfg: LlmConfig,
  args: {
    instruction?: string;
    text?: string;
    lang?: string;
    tone?: ToneStyle;
    apply?: boolean;
  },
): Promise<string> {
  const svc = new FeatureService(cfg);
  switch (feature) {
    case "draft":
      return svc.draft(args.instruction ?? "", args.apply ?? false);
    case "translate":
      return svc.translate(args.text ?? "", args.lang ?? "English");
    case "summarize":
      return svc.summarize();
    case "tone":
      return svc.tone(args.text ?? "", args.tone ?? "formal", args.apply ?? false);
    case "proofread":
      return svc.proofread(args.text ?? "", args.apply ?? false);
  }
}
